import { ArrowDownIcon } from "@src/assets/general-icons";
import React, { ReactElement, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, ButtonVariant } from "../button/Button";
import { clx } from "../utils/stringUtils";
import { useAppSelector } from "@src/store/hooks";
import styles from "./Dropdown.module.scss";

export interface IItems {
	preventClose?: boolean;
	id: number;
	label: string | ReactElement;
	icon?: ReactElement;
	value?: string | number;
	isDisabled?: boolean;
	onClick?: (value: IItems["value"], close: () => void) => void;
}

interface IDropdown extends React.ComponentProps<"div"> {
	label?: string;
	isDisabled?: boolean;
	containerWidth?: string;
	minHolderWidth?: number;
	placement?: "left" | "right";
	buttonText?: React.ReactNode;
	hideArrow?: boolean;
	items: Array<IItems>;
	dynamicText?: boolean;
	controlSize?: "m" | "sm";
	theme?: "primary" | "secondary";
	onItemSelect?: (item: IItems) => void;
	onClick?: () => void;
}

export const Dropdown: React.FC<IDropdown> = ({
	buttonText,
	items,
	minHolderWidth,
	containerWidth,
	label,
	placement = "left",
	dynamicText = false,
	isDisabled = false,
	hideArrow = false,
	controlSize = "m",
	theme = "primary",
	onItemSelect,
	onClick,
	...rest
}) => {
	const [open, setOpen] = useState(false);
	const [selected, setSelected] = useState(label ?? items?.[0]?.label);
	const [coords, setCoords] = useState<{ top: number; left: number; minWidth: number } | null>(null);
	const buttonRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const appTheme = useAppSelector((state) => state.settings.theme);

	const toggleOpen = () => {
		if (isDisabled) return;
		setOpen((prev) => !prev);
	};

	const updatePosition = useCallback(() => {
		const trigger = buttonRef.current?.getBoundingClientRect();
		if (!trigger) return;
		const panel = panelRef.current?.getBoundingClientRect();

		const gap = 6;
		const margin = 10;
		const panelWidth = panel?.width ?? trigger.width;
		const panelHeight = panel?.height ?? 0;

		let top = trigger.bottom + gap;
		let left = placement === "right" ? trigger.right - panelWidth : trigger.left;

		if (top + panelHeight + margin > window.innerHeight) {
			const upTop = trigger.top - gap - panelHeight;
			top = upTop >= margin ? upTop : Math.max(margin, window.innerHeight - panelHeight - margin);
		}

		if (left + panelWidth + margin > window.innerWidth) {
			left = Math.max(margin, window.innerWidth - panelWidth - margin);
		}
		if (left < margin) left = margin;

		setCoords({ top, left, minWidth: minHolderWidth ?? trigger.width });
	}, [placement, minHolderWidth]);

	useLayoutEffect(() => {
		if (!open) {
			setCoords(null);
			return;
		}
		updatePosition();
	}, [open, updatePosition]);

	useEffect(() => {
		if (!open) return;
		const handler = () => updatePosition();
		window.addEventListener("resize", handler);
		window.addEventListener("scroll", handler, true);
		return () => {
			window.removeEventListener("resize", handler);
			window.removeEventListener("scroll", handler, true);
		};
	}, [open, updatePosition]);

	const handleOutsideClick = useCallback((event: MouseEvent) => {
		const target = event.target as Node;
		if (buttonRef.current?.contains(target)) return;
		if (panelRef.current?.contains(target)) return;
		setOpen(false);
	}, []);

	useEffect(() => {
		if (open) {
			document.addEventListener("mousedown", handleOutsideClick);
		}
		return () => {
			document.removeEventListener("mousedown", handleOutsideClick);
		};
	}, [open, handleOutsideClick]);

	useEffect(() => {
		setSelected(label ?? items?.[0]?.label);
	}, [items.map((item) => item.label).join("")]);

	const mappedItems = items?.map((item) => {
		const { id, value, label, icon, onClick, preventClose } = item;
		return (
			<div
				className={clx({ [styles.item]: true, [styles.disabled]: !!item.isDisabled })}
				onClick={() => {
					onClick?.(value, toggleOpen);
					onItemSelect?.(item);
					if (dynamicText) setSelected(label);
					if (preventClose) return;
					toggleOpen();
				}}
				key={id}>
				{icon ? <span className={styles.itemIcon}>{icon}</span> : null}
				{label}
			</div>
		);
	});

	return (
		<div className={styles.main} {...rest}>
			{label && <div className={styles.label}>{label}</div>}
			<div
				style={{ width: containerWidth ?? undefined }}
				ref={buttonRef}
				className={clx(styles.dropdownContainer, styles[`${theme}Theme`])}>
				<Button
					style={{ width: containerWidth ?? "100%" }}
					onClick={() => {
						toggleOpen();
						if (isDisabled) return;
						onClick?.();
					}}
					disabled={isDisabled}
					controlSize={controlSize}
					variant={ButtonVariant.Secondary}
					className={styles.dropdownButtonContainer}>
					<div className={`${styles.dropdownButton}`}>
						<div className={styles.dropdownButtonText}>{dynamicText ? selected : (buttonText ?? "Default button")}</div>
						{hideArrow ? null : (
							<div>
								<ArrowDownIcon />
							</div>
						)}
					</div>
				</Button>
			</div>
			{open &&
				createPortal(
					<div className={`theme--${appTheme}`}>
						<div
							ref={panelRef}
							style={{
								top: coords?.top ?? 0,
								left: coords?.left ?? 0,
								minWidth: coords?.minWidth ?? minHolderWidth,
								visibility: coords ? "visible" : "hidden",
							}}
							className={clx(styles.itemsContainer, styles[`${theme}Theme`])}>
							{mappedItems ?? (
								<div className={styles.item} aria-hidden="true">
									"No options"
								</div>
							)}
						</div>
					</div>,
					document.body,
				)}
		</div>
	);
};
